import { ApolloClient } from "@apollo/client";
import { DocumentNode } from "graphql";
import { MimicLoaderOptions, MimicLoaderPageable, MimicLoaderProgressParams, MimicLoaderQueryResult, MimicLoaderQueryVariables } from "./types";

export const baseLoaderOptions: MimicLoaderOptions = {
  onProgress: e => console.log(JSON.stringify(e)),
  pageSize: 100,
  batchSize: 15,
  extractQueryData: (data) => Object.values(data || {})?.[0],
  collectItems: true,
};

export async function loadData<T>(query: DocumentNode, options?: MimicLoaderOptions<T>): Promise<T[]> {
  const client = options?.client as ApolloClient<any>;
  const extractQueryData = options?.extractQueryData || baseLoaderOptions.extractQueryData as (data: MimicLoaderQueryResult<T>) => MimicLoaderPageable<T>;
  const pageSize = options?.pageSize || baseLoaderOptions.pageSize as number;
  const batchSize = options?.batchSize || baseLoaderOptions.batchSize as number;
  const onProgress = options?.onProgress || baseLoaderOptions.onProgress as (e: MimicLoaderProgressParams) => any;
  const collectItems = options?.collectItems || baseLoaderOptions.collectItems;

  const { totalElements, totalPages, items } = extractQueryData((await client.query<MimicLoaderQueryResult<T>, MimicLoaderQueryVariables>({
    query,
    variables: {
      page: 0,
      size: pageSize,
      orders: options?.orders,
      filter: options?.filter,
      ...options?.queryAdditionalProps,
    },
  })).data);

  const progress: MimicLoaderProgressParams = {
    totalItems: totalElements,
    loadedItems: items.length,
    progress: items.length / totalElements,
  };
  const result = [] as T[];
  onProgress(progress);
  if (collectItems) {
    result.push(...items);
  }
  await options?.processItems?.(items, { totalItems: totalElements });

  if (totalPages !== 1) {
    let page = 1;

    for (let batchIterator = 0; batchIterator < Math.ceil(totalElements / batchSize / pageSize); batchIterator++) {
      onProgress(progress);
      const tasks = [] as Promise<T[]>[];
      for (let taskIterator = 0; taskIterator < batchSize; taskIterator++) {
        const currentPage = page++;
        tasks.push(new Promise<T[]>(async (resolve, reject) => {
          if (currentPage * pageSize < totalElements) {
            await new Promise(resolve => setTimeout(resolve, 0));
            const data = extractQueryData((await client.query<MimicLoaderQueryResult<T>, MimicLoaderQueryVariables>({
              query,
              variables: {
                page: currentPage,
                size: pageSize,
                orders: options?.orders,
                filter: options?.filter,
                ...options?.queryAdditionalProps,
              },
            })).data);

            try {
              await options?.processItems?.(data?.items, { totalItems: totalElements });
            } catch (err) {
              reject(err);
            }

            progress.loadedItems += data.items.length;
            progress.progress = progress.loadedItems / progress.totalItems;
            onProgress(progress);

            resolve(data.items);
          } else {
            resolve([]);
          }
        }));
      }
      const taskResults = await Promise.all(tasks);
      if (collectItems) {
        taskResults.forEach(taskResults => result.push(...taskResults));
      }
    }
  }

  return result;
}